import type { ReactNode } from 'react';
import type { ProductLine } from '@popcart/core/v1';
import { errorMessage, errorStatus } from '../api';
import { productArtFile, sceneFile, type SceneName } from './lib/art.js';
import './art.css';

const BASE = import.meta.env.BASE_URL;

/**
 * A product's picture: its photo when it has one, else its §10 drawing cut out on the line's
 * category colour (manual §11 "cut-outs on colour"), 3px Ink edge. Decorative: the row names it.
 */
export function ProductArt({
  name,
  line,
  category,
  src,
  className = '',
  alt = '',
}: {
  name: string;
  line: ProductLine | null | undefined;
  category?: string | null;
  src?: string | null;
  className?: string;
  alt?: string;
}) {
  if (src) return <img className={`pa ${className}`} src={src} alt={alt} loading="lazy" />;
  return (
    <span className={`pa pa-art pa-${line ?? 'mart'} ${className}`} role={alt ? 'img' : undefined} aria-label={alt || undefined} aria-hidden={alt ? undefined : true}>
      <img src={`${BASE}${productArtFile(name, line, category)}`} alt="" loading="lazy" data-art="product" />
    </span>
  );
}

/**
 * Empty state with its §10 scene (every scene hides a pop). Copy follows §13: say it with a wink,
 * one line, no more.
 */
export function Scene({ scene, title, children, action, compact = false }: { scene: SceneName; title: string; children?: ReactNode; action?: ReactNode; compact?: boolean }) {
  return (
    <div className={`empty-state scene${compact ? ' scene-compact' : ''}`}>
      <img className="scene-art" src={`${BASE}${sceneFile(scene)}`} alt="" data-scene={scene} />
      <h2 className="t-headline">{title}</h2>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

/** Error state with the dropped-skewer scene. §13: own it in word one, fix it in sentence two, no jokes. */
export function SceneError({ error, onRetry, title = 'This didn’t load', compact = false }: { error: unknown; onRetry?: () => void; title?: string; compact?: boolean }) {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const missing = !offline && errorStatus(error) === 404;
  const scene: SceneName = offline ? 'offline' : missing ? 'notFound' : 'error';
  return (
    <div className={`empty-state scene${compact ? ' scene-compact' : ''}`} role="alert">
      <img className="scene-art" src={`${BASE}${sceneFile(scene)}`} alt="" data-scene={scene} />
      <h2 className="t-headline">{offline ? 'You’re offline' : missing ? 'We can’t find that' : title}</h2>
      <p>{offline ? 'Check the shop’s Wi-Fi, then try again.' : errorMessage(error)}</p>
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}
